import SmsTemplate from "./sms-template";
import ServiceUtils from "./service-utils";
import PublicApi from "./public-api";
import Format from "./format";
import Field from "./field";
import Feedback from "./feedback";
import DocumentValue from "./document-value";
import Collection from "./collection";
import AppSettings from "./app-settings";
import Api from "./api";
import { digitsOnly, getEntryParams, normalize, replaceAll } from "../utils";
import ISubstitution from "../interface/substitution";
import ISmsTemplate from "../interface/sms-template";
import ISmsSendReponse from "../interface/sms-send-response";
import IServiceSms from "../interface/service-sms";
import IServiceGroupKey from "../interface/service-group-key";
import IRelationPathField from "../interface/relation-path-field";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import IApiField from "../interface/api/api-field";
import ServiceUtil from "../enum/service-util";
import ServiceName from "../enum/service-name";
import ServiceApiName from "../enum/service-api-name";
import LogicalValue from "../enum/logical-value";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import AppSetting from "../enum/app-setting";

class SmsSingleton {

    /* Public Methods */

    public getEmissions(entityId: number): IRelationPathField[] {
        return Field.getRelationPathsFields(entityId, [FieldType.phone], 2);
    }

    public getTestAddress(): string {
        return AppSettings.get(AppSetting.TestSms, '') as string;
    }

    public async send(tpl: ISmsTemplate, docs: IDocument[], save: boolean = false, testAddress: string = ''): Promise<ISmsSendReponse> {
        tpl = await SmsTemplate.prepareToSend(tpl, save);
        if (SmsTemplate.valid(tpl) && tpl.body && docs.length) {
            const result: ISmsSendReponse = {
                invalid: [],
                valid: []
            };
            testAddress = testAddress.trim();
            if (testAddress) {
                docs = [docs[0]];
                await AppSettings.set(AppSetting.TestSms, testAddress);
            }
            const subs: ISubstitution[] = ServiceUtils.getSourceSubstitutions(tpl.entityId, tpl.body);
            for (let i = 0; i < docs.length; i++) {
                const address = await this.getAddress(docs[i], tpl, testAddress);
                const msg: string = await this.prepareMessage(tpl.body, docs[i], subs);
                if (address) {
                    try {
                        const data: IServiceSms = {
                            src: normalize(tpl.sender),
                            text: normalize(msg),
                            dst: address
                        };
                        let groupKey: IServiceGroupKey | null = null;
                        if (!testAddress && tpl.id) {
                            groupKey = {
                                id: -1,
                                fCN: tpl.emissionPath,
                                split: [{ id: docs[i].id as number, key: address }],
                                action: Feedback.getCurrentActionString(tpl.id, tpl.feedbackPeriodicity)
                            };
                        }
                        await Api.svcExec(ServiceName.Sms, ServiceApiName.Send, groupKey, data);
                        result.valid.push(address);
                    } catch (e) {
                        result.invalid.push({
                            status: ErrorCode.ApiInternal,
                            document: docs[i],
                            address
                        });
                    }
                } else {
                    result.invalid.push({
                        status: ErrorCode.SmsInvalidAddressEmpty,
                        document: docs[i],
                        address
                    });
                }
            }
            await Collection.resetFeedback();
            return result;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Public Methods */

    public async sendSingle(from: string, to: string, msg: string): Promise<void> {
        await Api.svcExec(ServiceName.Sms, ServiceApiName.Send, null, {
            src: normalize(from),
            text: normalize(msg),
            dst: to
        });
    }

    /* Private Methods */

    private async getAddress(doc: IDocument, tpl: ISmsTemplate, testAddress: string): Promise<string> {
        let address: string;
        if (testAddress) {
            address = testAddress;
        } else {
            address = await DocumentValue.getByPath(doc, tpl.emissionPath) as string;
        }
        return digitsOnly(address);
    }

    private async prepareMessage(msg: string, doc: IDocument, subs: ISubstitution[]): Promise<string> {
        /* Value Substitutions */
        for (let i = 0; i < subs.length; i++) {
            const sub: ISubstitution = subs[i];
            const subUtil: string = `[${ServiceUtil.Sub}](${sub.pathId};;${sub.option})`;
            const value: IDocumentValue = await DocumentValue.getByPath(doc, sub.pathId);
            const format: string = Format.byPath(value, sub.pathId, sub.option);
            msg = replaceAll(msg, subUtil, format);
        }
        /* Question Substitutions - Positive */
        const entriesTrue: string[] = ServiceUtils.getEntries(msg, ServiceUtil.LinkTrue);
        for (let i = 0; i < entriesTrue.length; i++) {
            const entry: string = entriesTrue[i];
            const prm: (string | number)[] = getEntryParams(entry);
            const field: IApiField | null = Api.getField(prm[0] as number);
            let replacement: string = '';
            if (field) {
                replacement = await PublicApi.getQuestionAddressShorten(doc.id as number, field.id, LogicalValue.True);
            }
            msg = replaceAll(msg, entry, replacement);
        }
        /* Question Substitutions - Negative */
        const entriesFalse: string[] = ServiceUtils.getEntries(msg, ServiceUtil.LinkFalse);
        for (let i = 0; i < entriesFalse.length; i++) {
            const entry: string = entriesFalse[i];
            const prm: (string | number)[] = getEntryParams(entry);
            const field: IApiField | null = Api.getField(prm[0] as number);
            let replacement: string = '';
            if (field) {
                replacement = await PublicApi.getQuestionAddressShorten(doc.id as number, field.id, LogicalValue.False);
            }
            msg = replaceAll(msg, entry, replacement);
        }
        return msg;
    }

}

const Sms = new SmsSingleton();

export default Sms;